import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import CreateJs from 'preload-js'
import { Context as GeneralContext } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import Footer from '@/components/Footer'
import TextBlock from '@/components/TextBlock'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { insertBrAfterFirstSpace } from '@/utils/strings'
import WhistleblowingAccordion from '@/components/WhistleblowingAccordion'
import style from './style'

const useStyles = createUseStyles(style)

const About = ({
  stub,
  isEditorial,
}) => {
  const { getImageUrl, setPageAnimationReady } = useContext(GeneralContext)
  const classes = useStyles()
  const [load, setLoad] = useState(false)
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, mediaQueryKey } = useSelector((state) => ({
    current: state.page.current,
    mediaQueryKey: state.mediaQuery.key,
    page: state.page.pages[stub] || {},
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Main Images
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub, 'pages')
    if (page.featured_media !== 0) preload.current.loadFile(heroMedia)
  }

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
  }

  /*------------------------------
  If the page is loaded from redux, start to preload media
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) preloadMedia()
  }, [page])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) {
      initialize()
    }
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta head_json={page.yoast_head} />
    )
  }

  /*------------------------------
  Render Cover Home
  ------------------------------*/
  const renderCoverHome = () => {
    return (page?.featured_media && page?.title.rendered) && (
      <Hero
        aboveTheFold={true}
        img={(mediaQueryKey <= 1 && page.featured_media_mobile?.full[0]) ? page.featured_media_mobile?.full[0] : getImageUrl(page.featured_media, 'full', stub, 'pages')}
        text={page.content.rendered}
        title={insertBrAfterFirstSpace(page.title.rendered)}
        fontSize={isEditorial ? 'md' : 'lg'}
      />
    )
  }

  /*------------------------------
  Render Intro
  ------------------------------*/
  const renderIntroText = () => {
    return page?.banner_single_title || page?.banner_single_text ? (
      <div className={`${classes.wrapper} ${classes.intro}`}>
        <TextBlock
          title={page.banner_single_title}
          text={page.banner_single_text}
        />
      </div>
    ) : null
  }

  /*------------------------------
  Render Focus
  ------------------------------*/
  const renderFocus = (item, i) => {
    return (item?.focus_title) && (
      <Hero
        key={i.toString()}
        background={item.layout_choice}
        borderTitle
        fontSize="sm"
        img={getImageUrl(item.focus_img, 'full', stub, 'pages')}
        label={item.focus_label}
        link={item.focus_link}
        pretitle={item.focus_pretitle}
        text={item.focus_text}
        title={item.focus_title.replace(/\n/g, '<br />')}
        imgSize="crop"
        viewportHeight={!isEditorial}
      />
    )
  }

  /*------------------------------
  Render Page Elements Focus
  ------------------------------*/
  const renderPageElements = () => {
    return page?.page_builder_choice_focus?.length ? (
      page.page_builder_choice_focus.map((element, i) => renderFocus(element, i))
    ) : null
  }

  /*------------------------------
  Render Accordions
  ------------------------------*/
  const renderAccordions = () => {
    return page?.acf?.items?.length ? (
      <div className={`${classes.wrapper}`}>
        <WhistleblowingAccordion
          data={page.acf.items}
        />
      </div>
    ) : null
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer theme="dark" />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCoverHome()}
        {renderIntroText()}
        {renderPageElements()}
        {renderAccordions()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageAbout ${stub} ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default About
